/**
 * PROC-02 Output-envelope uniformity (INV-PACK-02, ENGINE-SPEC §4; changes C-PROC02-BOUND, C-G19-HEAD).
 *
 * Per-request generation budgets give chapters of near-identical length; human chapters breathe.
 * Units are the document's chapters with ≥ 150 prose words (§2.7 chapter detection; the pack
 * only recognised `##`/`###` headings); with fewer than 6 such chapters, scenes with ≥ 150
 * words; with fewer than 6 of those the channel abstains. CV uses the N−1 SD. Because a LOW CV is
 * the machine-like direction, the statistic that is scored is the one-sided UPPER 90% bound
 *
 *     raw = CV · (1 + 1.2816 · sqrt((1 + 2·CV²) / (2J)))
 *
 * (conservative: few units can only look less uniform), on the pack "low" ramp. κ = J/(J+6).
 * Structural channel: `units` carry each unit's prose word count (diagnostic), their `score`
 * repeats the document score.
 */
import type { UnitRange } from '../document/types.ts';
import { sdSample } from '../util/stats.ts';
import { abstainResult, derivePrior, languageGate, scoredResult } from './framework.ts';
import { SMALL, cvSe, fmt } from './helpers.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Ramp,
	RunContext,
	UnitEstimate,
} from './types.ts';

const RAMP: Ramp = [
	[0.18, 100],
	[0.3, 0],
];
const MIN_WORDS = 150;
const MIN_UNITS = 6;
const Z90 = 1.2816;

export const proc02Def: ChannelDef = {
	id: 'PROC-02',
	name: 'Output-envelope uniformity',
	family: 'ENVELOPE',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: true,
	estimator: 'structural',
	features: [{ id: 'cvBound', label: 'Upper 90% bound of the CV of chapter prose length' }],
	minExposure: MIN_UNITS,
	exposureUnit: 'units',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'CV of chapter length (upper 90% bound)',
	prior: derivePrior(RAMP, 1, 'units', 1.2),
	weight: 1.35,
	nd: 0.15,
	changes: ['C-PROC02-BOUND', 'C-G19-HEAD'],
};

function run(ctx: RunContext): ChannelResult {
	const def = proc02Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const usable = (list: UnitRange[]) =>
		list.map((u, index) => ({ u, index })).filter((x) => x.u.words >= MIN_WORDS);
	let kind: 'chapter' | 'scene' = 'chapter';
	let units = usable(doc.chapters);
	const nChapters = units.length;
	if (units.length < MIN_UNITS) {
		kind = 'scene';
		units = usable(doc.scenes);
	}
	const J = units.length;
	if (J < MIN_UNITS)
		return abstainResult(
			def,
			`insufficient units: ${nChapters} chapters and ${J} scenes with at least ${MIN_WORDS} words; need ${MIN_UNITS}`,
			notes
		);
	if (kind === 'scene') notes.push('fewer than 6 chapters: scenes used');

	const lens = units.map((x) => x.u.words);
	const mean = lens.reduce((a, b) => a + b, 0) / J;
	const sd = sdSample(lens);
	const cv = mean > 0 ? sd / mean : 0;
	const se = cvSe(cv, J);
	const bound = cv * (1 + Z90 * Math.sqrt((1 + 2 * cv * cv) / (2 * J)));
	const ci90: [number, number] = [Math.max(0, cv - 1.645 * se), cv + 1.645 * se];
	const kappa = J / (J + 6);

	const res = scoredResult(def, {
		K: J,
		E: J,
		raw: bound,
		estimate: bound,
		ci90,
		deviation: (def.direction * (cv - def.prior.mu)) / Math.max(se, SMALL),
		phi: 1,
		kappa,
		notes,
		features: { cv, cvBound: bound, units: J, meanWords: mean, sdWords: sd },
		summary: [
			`n=${J} (${kind}s)`,
			`mean=${Math.round(mean)}w`,
			`sd=${Math.round(sd)}w`,
			`min=${Math.min(...lens)}`,
			`max=${Math.max(...lens)}`,
			`CV=${fmt(cv, 3)}, upper 90% bound ${fmt(bound, 3)}`,
		],
	});
	const est: UnitEstimate[] = units.map((x) => ({
		kind,
		index: x.index,
		paraStart: x.u.paraStart,
		paraEnd: x.u.paraEnd,
		K: x.u.words,
		E: x.u.words,
		estimate: x.u.words,
		ci90: [x.u.words, x.u.words],
		score: res.doc.score,
		elevated: false,
		pRef: 1,
	}));
	res.units = est;
	return res;
}

export const proc02: ChannelImpl = { def: proc02Def, phase: 'derived', run };
